import React, { useCallback } from 'react';
import { Box, Typography, Paper, styled, alpha, Button } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { useDropzone } from 'react-dropzone';

const UploadWrapper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  backgroundColor: alpha(theme.palette.background.paper, 0.6),
  border: `1.5px dashed ${alpha(theme.palette.primary.main, 0.2)}`,
  borderRadius: '16px',
  cursor: 'pointer',
  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '&:hover': {
    backgroundColor: alpha(theme.palette.background.paper, 0.8),
    borderColor: theme.palette.primary.main,
    transform: 'translateY(-2px)',
    '& .upload-icon': {
      transform: 'translateY(-4px) scale(1.05)',
      color: theme.palette.primary.main
    }
  }
}));

const UploadContent = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
  gap: theme.spacing(2),
  padding: theme.spacing(2)
}));

const IconWrapper = styled(Box)(({ theme }) => ({
  width: '56px',
  height: '56px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '16px',
  backgroundColor: alpha(theme.palette.primary.main, 0.06),
  color: theme.palette.primary.light,
  marginBottom: theme.spacing(1),
  '& .upload-icon': {
    fontSize: '28px',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
  }
}));

const UploadTitle = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.primary,
  fontWeight: 600,
  fontSize: '0.95rem',
  letterSpacing: '0.01em'
}));

const UploadDescription = styled(Typography)(({ theme }) => ({
  color: alpha(theme.palette.text.secondary, 0.9),
  fontSize: '0.85rem',
  lineHeight: 1.5,
  maxWidth: '200px',
  margin: '0 auto'
}));

const FileInfo = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  marginTop: theme.spacing(2),
  padding: theme.spacing(1.5, 2),
  borderRadius: '10px',
  backgroundColor: alpha(theme.palette.primary.main, 0.06),
  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
  transition: 'all 0.2s ease',
  '& .file-icon': {
    color: theme.palette.primary.main,
    fontSize: '20px'
  },
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.08),
    transform: 'translateY(-1px)'
  }
}));

const FileName = styled(Typography)(({ theme }) => ({
  color: theme.palette.primary.main,
  fontSize: '0.85rem',
  fontWeight: 500,
  flex: 1,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap'
}));

const DownloadButton = styled(Button)(({ theme }) => ({
  minWidth: 'auto',
  padding: theme.spacing(0.5, 1),
  fontSize: '0.75rem',
  textTransform: 'none',
  color: theme.palette.primary.main,
  borderColor: theme.palette.primary.main,
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.08),
    borderColor: theme.palette.primary.dark
  }
}));

const FileUploadZone = ({ title, description, icon, file, onFileChange, accept, onDownload }) => {
  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles?.length > 0) {
      onFileChange(acceptedFiles[0]);
    }
  }, [onFileChange]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: accept ? { 'application/pdf': ['.pdf'], 'application/msword': ['.doc', '.docx'], 'application/vnd.ms-excel': ['.xls'], 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'] } : undefined,
    maxFiles: 1
  });

  return (
    <UploadWrapper
      elevation={0}
      {...getRootProps()}
      sx={isDragActive ? {
        borderColor: 'primary.main',
        backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.04),
        transform: 'scale(1.01)'
      } : {}}
    >
      <input {...getInputProps()} />
      <UploadContent>
        <IconWrapper>
          {icon || <CloudUploadIcon className="upload-icon" />}
        </IconWrapper>
        <Box>
          <UploadTitle>{title}</UploadTitle>
          <UploadDescription>{description}</UploadDescription>
        </Box>
        {file && (
          <FileInfo>
            {icon}
            <FileName>{file.name}</FileName>
            {(file.existingUrl || file.s3Url || onDownload || file.s3Key) && (
              <DownloadButton
                variant="outlined"
                size="small"
                startIcon={<FileDownloadIcon />}
                onClick={(e) => {
                  e.stopPropagation();
                  if (onDownload) {
                    onDownload();
                    return;
                  }
                  const downloadUrl = file.s3Url || file.existingUrl;
                  if (downloadUrl) {
                    window.open(downloadUrl, '_blank');
                  }
                }}
              >
                Download
              </DownloadButton>
            )}
          </FileInfo>
        )}
      </UploadContent>
    </UploadWrapper>
  );
};

export default FileUploadZone; 